import { roundXp } from "../../config/supernovaXp";
import { buildExitQuote, buildStakeRows } from "./supernovaExitQuote";
import { METRIC } from "./supernovaSummary";

/**
 * supernovaXpBalance — what the XP tile is allowed to say, and why it usually
 * can't say "balance".
 *
 * No React, no I/O. It takes the stake lots the chain read returned, the closed
 * lots the event-log read rebuilt, and produces one status-carrying view.
 *
 * A MEMBER'S XP HAS TWO HALVES:
 *
 *   BANKED   points already settled from stakes that have CLOSED. This half used
 *            to be a null stub, because a closed lot is gone from getStaked and
 *            `stakes()` keeps its fields but not the moment it closed.
 *            supernovaClosedLots now rebuilds it from the contract's own event
 *            log, so it is REAL — with a caveat that stays on screen: a closed
 *            lot we cannot rebuild confidently is excluded and COUNTED, and the
 *            tile footnotes the count.
 *   ACCRUING  points the member's OPEN lots have earned so far. It comes out of
 *            config/supernovaXp via supernovaExitQuote, the same functions the
 *            Active stakes table renders.
 *
 * BOTH HALVES ARE RATED BY THE SAME MODEL. A closed lot is quoted with
 * `buildExitQuote(lot, lot.exitAtMs)` — the exact call the unstake modal makes,
 * with the clock set to the moment the lot actually closed. Matured lots keep
 * the full term; early exits get the re-rate and the decay penalty. Nothing here
 * re-derives a multiplier or a penalty, so banked and accruing cannot drift.
 *
 * SO THE TILE RENAMES ITSELF RATHER THAN OVERSTATE. Where the banked half is
 * genuinely unknown — the closed-lot read failed, or hasn't run — the accrual
 * figure is NOT a balance, and presenting it as one would be a wrong number that
 * looks right. The view comes back labelled "XP accrued" with the missing half
 * stated on screen, and only becomes "XP balance" once both halves are known.
 *
 * COMPLIANCE: XP is an in-app points balance with no monetary value. Nothing
 * here is a return, a yield, income, a price, or a projection.
 */

/** The tile's two possible labels — see the module comment. */
export const XP_BALANCE_LABEL = "XP balance";
export const XP_ACCRUED_LABEL = "XP accrued";

/**
 * The banked base when no caller supplies one.
 *
 * Still null, and still deliberately not a number: null means UNKNOWN, and the
 * tile is built to say so. What changed is that it is no longer the only
 * answer — pass `closed` (a supernovaChain.getClosedStakes result) and the
 * banked half is derived from the contract's own log; pass `bankedBaseXp` or the
 * engine's complete `engineXp` and those win. With none of the three, the tile
 * falls back to accrual on the open lots and renames itself.
 */
export const BANKED_BASE_XP = null;

export const BANKED_BASE_NOTE =
  "XP banked from stakes you've already closed isn't connected yet. This " +
  "figure is what your open stakes have accrued so far — not your lifetime " +
  "total.";

export const BANKED_BASE_SHORT_NOTE =
  "Banked XP from closed stakes isn't connected yet.";

/** The footnote when the banked half is real but not complete. */
export function bankedExcludedNote(excluded) {
  const lots = `${excluded.toLocaleString("en-US")} closed ${
    excluded === 1 ? "stake" : "stakes"
  }`;
  return `${lots} couldn't be read from the contract's history, so ${
    excluded === 1 ? "it isn't" : "they aren't"
  } in this total.`;
}

export const ACCRUAL_UNAVAILABLE_NOTE =
  "XP accrues on your open stake lots. Those aren't connected yet, so there's " +
  "nothing for us to add up — your on-chain stakes are unaffected.";

export const ACCRUAL_UNRATEABLE_MESSAGE =
  "We couldn't rate any of your open stakes, so we can't total what they've accrued.";

const isNum = (n) => typeof n === "number" && Number.isFinite(n);

/**
 * The banked half: what each CLOSED lot actually kept, at the moment it closed.
 *
 * `buildExitQuote(lot, lot.exitAtMs)` is the unstake modal's own call with the
 * clock moved back to the exit — so a matured lot banks its full term and an
 * early exit banks the re-rated figure after the decay penalty, by the same
 * arithmetic the member was quoted on the way out. A lot the model refuses to
 * rate (`valid: false`) is counted as excluded, never totalled as zero.
 *
 * @param {Object|null} closed a supernovaChain.getClosedStakes result
 * @returns {{bankedXp: number, lotsBanked: number, lotsExcluded: number}}
 */
export function bankedXpFromClosedLots(closed) {
  const lots = Array.isArray(closed?.lots) ? closed.lots : [];
  /* Lots the READ could not rebuild (no opening log, no block timestamp) are
     already counted by getClosedStakes; unrateable ones are added here. */
  let excluded = isNum(closed?.excluded) ? closed.excluded : 0;
  let banked = 0;
  let counted = 0;

  lots.forEach((lot) => {
    let quote;
    try {
      quote = buildExitQuote(lot, lot.exitAtMs);
    } catch (e) {
      quote = null;
    }
    if (!quote || !quote.valid || !isNum(quote.keptXp)) {
      excluded += 1;
      return;
    }
    banked += quote.keptXp;
    counted += 1;
  });

  return {
    bankedXp: roundXp(banked, 6),
    lotsBanked: counted,
    lotsExcluded: excluded,
  };
}

/**
 * "accruing from 3 open stakes" — the caption under the figure, extended to
 * name the banked half once there is one: "banked from 2 closed · accruing from
 * 3 open stakes". A member has to be able to see which halves the figure is
 * made of without opening anything.
 */
function accrualMeta(ratedCount, unreadableCount, banked = null) {
  const stakes = `${ratedCount.toLocaleString("en-US")} open ${
    ratedCount === 1 ? "stake" : "stakes"
  }`;
  const accruing =
    ratedCount === 0 && unreadableCount === 0
      ? "no open stakes accruing"
      : unreadableCount > 0
      ? `accruing from ${stakes} · ${unreadableCount.toLocaleString("en-US")} we couldn't read`
      : `accruing from ${stakes}`;

  const bankedLots = banked && isNum(banked.lotsBanked) ? banked.lotsBanked : 0;
  if (bankedLots === 0) return accruing;
  return `banked from ${bankedLots.toLocaleString("en-US")} closed · ${accruing}`;
}

/**
 * @typedef {Object} XpBalanceView
 * @property {"engine"|"accrual"|null} source  where the figure came from
 * @property {string} status        METRIC status the tile renders
 * @property {string} label         XP_BALANCE_LABEL or XP_ACCRUED_LABEL
 * @property {number|null} value    the figure to show; null unless status READY
 * @property {string|null} meta     caption under the figure
 * @property {string|null} note     why a half (or the whole thing) is missing
 * @property {string|null} message  member-safe copy for the error state
 * @property {number|null} accruedXp   the open-lot half, when we have it
 * @property {number|null} bankedXp    the closed-lot half, when we have it
 * @property {boolean} bankedKnown
 * @property {number|null} totalXp     both halves; null unless both are known
 * @property {number|null} lotsRated
 * @property {number|null} lotsUnreadable
 * @property {number|null} lotsBanked     closed lots that made it into `bankedXp`
 * @property {number|null} lotsExcluded   closed lots we couldn't rebuild
 * @property {string|null} bankedNote     the footnote, when something is missing
 */

/**
 * @param {Object} input
 * @param {Object|null} [input.staked]        getStaked() result
 * @param {string} [input.stakedStatus]       METRIC status for that read
 * @param {string|null} [input.stakedNote]    why stakes are unavailable
 * @param {string|null} [input.stakedMessage] member-safe copy for a failed read
 * @param {Object|null} [input.closed]        getClosedStakes() result
 * @param {string} [input.closedStatus]       METRIC status for that read
 * @param {number|null} [input.bankedBaseXp]  an explicit banked total; wins over
 *        `closed`, for a caller that already has the engine's banked figure
 * @param {number|null} [input.engineXp]      the engine's complete XP total
 * @param {string} [input.engineStatus]       METRIC status for that number
 * @param {number|Date} [input.nowMs]         injectable clock
 * @returns {XpBalanceView}
 */
export function buildXpBalance(input = {}) {
  const {
    staked = null,
    stakedStatus = METRIC.UNAVAILABLE,
    stakedNote = null,
    stakedMessage = null,
    closed = null,
    closedStatus = METRIC.UNAVAILABLE,
    bankedBaseXp = BANKED_BASE_XP,
    engineXp = null,
    engineStatus = METRIC.UNAVAILABLE,
    nowMs = Date.now(),
  } = input;

  const now = nowMs instanceof Date ? nowMs.getTime() : nowMs;

  /* ── The banked half ─────────────────────────────────────────────────────
     An explicit `bankedBaseXp` wins: a caller holding the engine's own banked
     figure knows more than the log does. Otherwise a READY closed-lot read is
     summed here. Any other closed status leaves the half UNKNOWN — not zero,
     which is why the failed and unavailable branches fall through untouched. */
  const banked = isNum(bankedBaseXp)
    ? { bankedXp: bankedBaseXp, lotsBanked: null, lotsExcluded: null }
    : closedStatus === METRIC.READY
    ? bankedXpFromClosedLots(closed)
    : null;
  const bankedKnown = Boolean(banked);
  const excluded = banked && isNum(banked.lotsExcluded) ? banked.lotsExcluded : 0;

  const base = {
    source: null,
    status: METRIC.UNAVAILABLE,
    label: XP_BALANCE_LABEL,
    value: null,
    meta: null,
    note: null,
    message: null,
    accruedXp: null,
    bankedXp: bankedKnown ? banked.bankedXp : null,
    bankedKnown,
    totalXp: null,
    lotsRated: null,
    lotsUnreadable: null,
    lotsBanked: banked ? banked.lotsBanked : null,
    lotsExcluded: banked ? banked.lotsExcluded : null,
    /* The one caption that survives every branch: either the banked half is
       missing entirely, or it is here but incomplete. Silence means complete. */
    bankedNote: bankedKnown
      ? excluded > 0
        ? bankedExcludedNote(excluded)
        : null
      : BANKED_BASE_SHORT_NOTE,
  };

  /* ── The engine's own total wins, because it is the only figure that
        includes both halves. ─────────────────────────────────────────────── */
  if (engineStatus === METRIC.READY && isNum(engineXp)) {
    return {
      ...base,
      source: "engine",
      status: METRIC.READY,
      value: engineXp,
      totalXp: engineXp,
      meta: "in-app points · no monetary value",
      /* The engine's total already contains both halves, so any note about a
         missing or partial banked half would be a false disclaimer. */
      bankedNote: null,
    };
  }
  if (engineStatus === METRIC.LOADING) return { ...base, status: METRIC.LOADING };

  /* ── Otherwise: accrual on the open lots, which is only half the story. ── */
  switch (stakedStatus) {
    case METRIC.LOADING:
      return { ...base, status: METRIC.LOADING };
    case METRIC.IDLE:
      return { ...base, status: METRIC.IDLE };
    case METRIC.ERROR:
      return {
        ...base,
        status: METRIC.ERROR,
        message: stakedMessage || "Couldn't read your stakes, so we can't total your XP.",
      };
    case METRIC.READY:
      break;
    default:
      return { ...base, note: stakedNote || ACCRUAL_UNAVAILABLE_NOTE };
  }

  /* buildStakeRows is what the Active stakes table renders, so the tile and the
     table cannot disagree about a single lot. An unreadable lot contributes
     nothing and is COUNTED, never silently dropped to zero. */
  const rows = buildStakeRows(staked, now);
  const rated = rows.filter((r) => r.readable);
  const unreadable = rows.length - rated.length;

  if (rated.length === 0 && unreadable > 0) {
    return {
      ...base,
      source: "accrual",
      status: METRIC.ERROR,
      message: ACCRUAL_UNRATEABLE_MESSAGE,
      lotsRated: 0,
      lotsUnreadable: unreadable,
    };
  }

  const accruedXp = roundXp(
    rated.reduce((sum, r) => sum + r.earnedXp, 0),
    6
  );
  const totalXp = bankedKnown ? roundXp(base.bankedXp + accruedXp, 6) : null;

  return {
    ...base,
    source: "accrual",
    status: METRIC.READY,
    /* The rename that keeps this honest — see the module comment. */
    label: bankedKnown ? XP_BALANCE_LABEL : XP_ACCRUED_LABEL,
    value: bankedKnown ? totalXp : accruedXp,
    accruedXp,
    totalXp,
    meta: accrualMeta(rated.length, unreadable, banked),
    note: bankedKnown ? base.bankedNote : BANKED_BASE_NOTE,
    lotsRated: rated.length,
    lotsUnreadable: unreadable,
  };
}

export default buildXpBalance;
